import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const SIDE_IMG = "https://images.pexels.com/photos/18051278/pexels-photo-18051278.jpeg";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname;
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form.name, form.email, form.password);
      navigate(from || "/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen grid md:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center px-6 py-24 md:py-0 order-2 md:order-1">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="w-full max-w-sm">
          <p className="label text-accent mb-4">/ Create account</p>
          <h1 className="font-display text-4xl font-light tracking-tight mb-10">Join the tide</h1>

          {error && <p className="mb-6 text-sm text-destructive border border-destructive px-4 py-3" data-testid="register-error">{error}</p>}

          <form onSubmit={submit} className="space-y-6" data-testid="register-form">
            <div>
              <label className="label text-muted-foreground block mb-2">Name</label>
              <input name="name" required value={form.name} onChange={onChange} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors" data-testid="register-name" />
            </div>
            <div>
              <label className="label text-muted-foreground block mb-2">Email</label>
              <input name="email" type="email" required value={form.email} onChange={onChange} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors" data-testid="register-email" />
            </div>
            <div>
              <label className="label text-muted-foreground block mb-2">Password</label>
              <input name="password" type="password" required minLength={6} value={form.password} onChange={onChange} className="w-full bg-background border border-border px-4 py-3 text-sm focus:outline-none focus:border-accent transition-colors" data-testid="register-password" />
              <p className="mt-2 text-xs text-muted-foreground">At least 6 characters.</p>
            </div>
            <button type="submit" disabled={busy} className="w-full h-14 bg-primary text-primary-foreground label hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="register-submit">
              {busy ? "Creating…" : "Create account"} <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          </form>

          <p className="mt-8 text-sm text-muted-foreground">
            Already have an account? <Link to="/login" className="text-accent border-b border-accent" data-testid="to-login">Sign in</Link>
          </p>
          <p className="mt-10 label text-muted-foreground/70 tracking-[0.2em]">Research Use Only · Not For Human Consumption</p>
        </motion.div>
      </div>

      <div className="relative hidden md:block bg-primary overflow-hidden order-1 md:order-2">
        <img src={SIDE_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-primary/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-primary-foreground text-right items-end">
          <span className="font-display text-2xl">Coastal Chemistry</span>
          <h2 className="font-display text-5xl font-light leading-[0.95] tracking-tight max-w-sm">Precision, from the first drop.</h2>
        </div>
      </div>
    </main>
  );
}
